import { ref, computed, watch, onScopeDispose } from 'vue';
import { DEFAULT_RANGE, RANGE_MS, type TimeRange } from './useFilters.js';
import { SCRAPE_INTERVAL_MS } from '../lib/metric-exploration.js';

/**
 * The dashboard clock (§3.4): ONE time-range + ONE live/paused flag drive every
 * tile on the dashboard. Tiles never own timers — they are pure functions of
 * `(resolvedConfig, window)` and simply refetch when `from`/`to` move.
 *
 * Live: a single interval (the ≥15s scrape cadence, amendments §5 — dashboards
 * refresh scrape-aligned, NOT on the 2s log cadence) advances `to = Date.now()`.
 * Paused: the window freezes exactly where it was. Changing the range re-anchors
 * `to` to now (matching how MetricsView computes its window on each query).
 */
export function useDashboardClock(initial: TimeRange = DEFAULT_RANGE) {
  const range = ref<TimeRange>(initial);
  const live = ref(true);
  const to = ref(Date.now());
  const from = computed(() => to.value - RANGE_MS[range.value]);

  let timer: ReturnType<typeof setInterval> | undefined;
  function start() {
    stop();
    timer = setInterval(() => {
      if (live.value) to.value = Date.now();
    }, SCRAPE_INTERVAL_MS);
  }
  function stop() {
    if (timer) clearInterval(timer);
    timer = undefined;
  }

  /** Change the range (re-anchors the window to now). */
  function setRange(r: TimeRange) {
    range.value = r;
    to.value = Date.now();
  }

  /** Re-seed the clock for a newly opened dashboard (its `time_default`). */
  function reset(r: TimeRange) {
    range.value = r;
    live.value = true;
    to.value = Date.now();
  }

  // Resuming live snaps the window forward to now immediately (no waiting for
  // the next tick to catch up).
  watch(live, (on) => {
    if (on) to.value = Date.now();
  });

  start();
  onScopeDispose(stop);

  return { range, live, from, to, setRange, reset };
}
